import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  NgZone
} from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { AlertEventState, AlertSeverityLevel, alertSeverityLabel } from 'src/app/enums/alert.enum';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { DateTimeRange } from 'src/app/models/dateRange.model';
import { msToBaseInterval, positiveDeltas, upperMedian } from 'src/app/shared/utils/date-axis-base-interval.util';

export interface ChartDataPoint {
  timestamp: number;
  value: number | null;
}

export interface ChartSeriesInput {
  data: ChartDataPoint[];
  paramId: number;
  paramName: string;
  paramUnit: string;
  nodeId: string;
  nodeName: string;
  nodeIndex: number;
  parameterIndex?: number;
  axisOverride?: 0 | 1 | 2;
  color?: string;
  seriesIndex?: number;
  hidden?: boolean;
}

export interface NotificationPoint {
  timestamp: number;
  label: string;
  severity: AlertSeverityLevel | number;
  state: AlertEventState;
  sentAt?: number;
  channel?: string;
}

export interface AlertPeriodOverlay {
  id: string;
  notificationName: string;
  severity: AlertSeverityLevel | number;
  state: AlertEventState;
  startedAt: number;
  endedAt: number;
  sentAt?: number;
  peakAt?: number;
  peakValue?: number;
  channel?: string;
}

export interface ThresholdOverlayLine {
  value: number;
  severity: AlertSeverityLevel | number;
  label: string;
  colorHex?: string;
}

export interface ChartPlotBounds {
  leftPx: number;
  rightPx: number;
  widthPx: number;
}

export interface YAxisControl {
  axisIndex: number;
  label: string;
  min: number | null;
  max: number | null;
  manual: boolean;
}

export interface NoteRangeOverlay {
  id: string;
  start: number;
  end: number;
}

@Component({
  standalone: false,
  selector: 'app-node-analytics-chart',
  templateUrl: './node-analytics-chart.component.html',
  styleUrls: ['./node-analytics-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NodeAnalyticsChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('chartDiv') chartDiv!: ElementRef<HTMLDivElement>;

  @Input() seriesData: ChartSeriesInput[] = [];
  @Input() timeCursor: number = 0;
  @Input() notifications: NotificationPoint[] = [];
  @Input() alertPeriods: AlertPeriodOverlay[] = [];
  @Input() thresholds: ThresholdOverlayLine[] = [];
  @Input() selectedRange: DateTimeRange | null = null;
  @Input() noteRanges: NoteRangeOverlay[] = [];
  @Input() selectedNoteId: string | null = null;
  @Input() showAlertPeriods = true;
  @Input() showNotifications = true;
  @Input() showThresholds = false;
  @Input() showNotes = false;
  @Input() showMinMaxBand = false;
  @Input() rangePickMode = false;

  @Output() zoomChange = new EventEmitter<DateTimeRange | null>();
  @Output() cursorMove = new EventEmitter<number>();
  @Output() plotBoundsChange = new EventEmitter<ChartPlotBounds>();
  @Output() rangeSelection = new EventEmitter<DateTimeRange>();
  @Output() rangePicked = new EventEmitter<DateTimeRange>();
  @Output() rangePickProgress = new EventEmitter<number | null>();
  @Output() yAxisRangeChange = new EventEmitter<number>();

  private root: am5.Root | null = null;
  protected chart: am5xy.XYChart | null = null;
  protected xAxis: am5xy.DateAxis<am5xy.AxisRendererX> | null = null;
  private timeCursorRange: am5.DataItem<am5xy.IDateAxisDataItem> | null = null;
  private lastCursorEmit = 0;
  private axisIndexYAxisMap = new Map<number, am5xy.ValueAxis<am5xy.AxisRendererY>>();
  private manualYAxisIndexes = new Set<number>();
  private lastAxisValueRanges = new Map<number, { min: number; max: number }>();
  private lastActiveAxisIndexes = new Set<number>();
  private axisIndexLabelMap = new Map<number, am5.Label>();
  private lineSeriesMap = new Map<string, am5xy.LineSeries>();
  private bandSeriesMap = new Map<string, am5xy.LineSeries>();
  private notificationSeries: am5xy.LineSeries | null = null;
  private alertPeakSeries: am5xy.LineSeries | null = null;
  private zoomTimer: ReturnType<typeof setTimeout> | null = null;
  private alertRangeItems: am5.DataItem<am5xy.IDateAxisDataItem>[] = [];
  private thresholdRangeItems: am5.DataItem<am5xy.IValueAxisDataItem>[] = [];
  private selectedRangeItem: am5.DataItem<am5xy.IDateAxisDataItem> | null = null;
  private noteRangeItems: am5.DataItem<am5xy.IDateAxisDataItem>[] = [];
  private lastPlotBounds: ChartPlotBounds | null = null;
  private isCursorRangeSelecting = false;
  protected rangePickFirstTs: number | null = null;
  private rangePickGuideItem: am5.DataItem<am5xy.IDateAxisDataItem> | null = null;
  private yAxisSettleTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private isSettlingYAxis = false;

  private readonly COLORS = [
    0x5B6BD5, 0xE57C3A, 0x44A98C, 0xC0443A, 0x8B5CF6,
    0xD4A017, 0x2E86AB, 0x6B7280, 0xE91E8C, 0x00B4D8
  ];

  private readonly PARAMETER_DASHES: (number[] | undefined)[] = [
    undefined, [8, 4], [4, 4], [2, 4], [6, 2]
  ];

  constructor(private ngZone: NgZone, private dateFormatService: DateFormatService) { }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => this.buildChart());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['seriesData'] && this.root) {
      this.ngZone.runOutsideAngular(() => this.syncSeries());
    }
    if (changes['notifications'] && this.root) {
      this.ngZone.runOutsideAngular(() => this.syncNotifications());
    }
    if ((changes['alertPeriods'] || changes['showAlertPeriods']) && this.root) {
      this.ngZone.runOutsideAngular(() => this.syncAlertPeriods());
    }
    if ((changes['thresholds'] || changes['showThresholds']) && this.root) {
      this.ngZone.runOutsideAngular(() => this.syncThresholds());
    }
    if (changes['selectedRange'] && this.root) {
      this.ngZone.runOutsideAngular(() => this.syncSelectedRange());
    }
    if ((changes['noteRanges'] || changes['selectedNoteId'] || changes['showNotes']) && this.root) {
      this.ngZone.runOutsideAngular(() => this.syncNoteRanges());
    }
    if (changes['showNotifications'] && this.root) {
      this.ngZone.runOutsideAngular(() => this.syncNotifications());
    }
    if (changes['showMinMaxBand'] && this.root) {
      this.ngZone.runOutsideAngular(() => this.applyBandVisibility());
    }
    if (changes['rangePickMode'] && this.root) {
      this.ngZone.runOutsideAngular(() => this.applyRangePickMode());
    }
    if (changes['timeCursor'] && !changes['timeCursor'].firstChange && this.timeCursor > 0) {
      this.ngZone.runOutsideAngular(() => this.updateTimeCursorLine());
    }
  }

  ngOnDestroy(): void {
    this.disposeRoot();
  }

  zoomFit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.manualYAxisIndexes.clear();
      this.applyYAxisBounds(this.lastAxisValueRanges, this.lastActiveAxisIndexes);
      this.xAxis?.zoom(0, 1);
    });
  }

  /**
   * Active value axes with their current (data-derived or manual) range, used to render the
   * per-axis Y-range inputs in the toolbar. One entry per active axis index so it is always
   * unambiguous which axis a range applies to; inactive axes are omitted.
   */
  getYAxisControls(): YAxisControl[] {
    const controls: YAxisControl[] = [];
    this.axisIndexYAxisMap.forEach((yAxis, axisIdx) => {
      if (!this.lastActiveAxisIndexes.has(axisIdx)) {
        return;
      }
      const isManual = this.manualYAxisIndexes.has(axisIdx);
      const currentMin = yAxis.get('min');
      const currentMax = yAxis.get('max');
      const dataRange = this.lastAxisValueRanges.get(axisIdx);
      // For a manual axis the pinned min/max IS what the user sees, so prefill from it. For an
      // auto axis yAxis.get('min'/'max') is the padded value (e.g. dataMin - 8%); prefill from the
      // cached unpadded data extent instead so the inputs match the data the chart is drawing.
      controls.push({
        axisIndex: axisIdx,
        label: this.axisIndexLabelMap.get(axisIdx)?.get('text') ?? '',
        min: isManual
          ? (typeof currentMin === 'number' ? currentMin : dataRange?.min ?? null)
          : dataRange?.min ?? (typeof currentMin === 'number' ? currentMin : null),
        max: isManual
          ? (typeof currentMax === 'number' ? currentMax : dataRange?.max ?? null)
          : dataRange?.max ?? (typeof currentMax === 'number' ? currentMax : null),
        manual: isManual,
      });
    });
    return controls;
  }

  /**
   * Pins a value axis to a user-supplied min/max and flags it as manually controlled so
   * applyYAxisBounds() stops overwriting it on subsequent data/series/zoom updates. Composes
   * with the X zoom/scrollbar — neither resets the other.
   */
  setYAxisRange(axisIndex: number, min: number, max: number): void {
    const yAxis = this.axisIndexYAxisMap.get(axisIndex);
    if (!yAxis || !Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.manualYAxisIndexes.add(axisIndex);
      yAxis.set('min', min);
      yAxis.set('max', max);
    });
  }

  /** Clears an axis' manual override and returns it to the auto/data-derived range. */
  resetYAxisRange(axisIndex: number): void {
    if (!this.axisIndexYAxisMap.has(axisIndex)) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.manualYAxisIndexes.delete(axisIndex);
      this.applyYAxisBounds(this.lastAxisValueRanges, this.lastActiveAxisIndexes);
    });
  }

  /** The visible time window, or null before the axis has data. */
  getXRange(): DateTimeRange | null {
    const start = this.xAxis?.getPrivate('selectionMin') as number | undefined;
    const end = this.xAxis?.getPrivate('selectionMax') as number | undefined;
    return start != null && end != null && end > start ? { start, end } : null;
  }

  /** Returns the time axis to its full range without touching any manually scaled value axis. */
  resetXRange(): void {
    this.ngZone.runOutsideAngular(() => this.xAxis?.zoom(0, 1));
  }

  zoomToRange(startTime: number, endTime: number, padded = true): void {
    this.ngZone.runOutsideAngular(() => {
      if (!this.xAxis) {
        return;
      }

      const start = Math.min(startTime, endTime);
      const end = Math.max(startTime, endTime);
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
        return;
      }

      const axisMin = this.xAxis.getPrivate('min') as number | undefined;
      const axisMax = this.xAxis.getPrivate('max') as number | undefined;
      if (axisMin == null || axisMax == null || axisMax <= axisMin) {
        return;
      }

      const rangeWidth = end - start;
      const padding = padded ? Math.max(rangeWidth * 0.08, 1000) : 0;
      const targetStart = Math.max(axisMin, start - padding);
      const targetEnd = Math.min(axisMax, end + padding);
      if (targetEnd <= targetStart) {
        return;
      }

      const normalizedStart = (targetStart - axisMin) / (axisMax - axisMin);
      const normalizedEnd = (targetEnd - axisMin) / (axisMax - axisMin);

      this.xAxis.zoom(
        this.clamp(normalizedStart, 0, 1),
        this.clamp(normalizedEnd, 0, 1),
      );
    });
  }

  private disposeRoot(): void {
    if (this.zoomTimer) clearTimeout(this.zoomTimer);
    this.yAxisSettleTimers.forEach((timer) => clearTimeout(timer));
    this.yAxisSettleTimers.clear();
    if (this.root) {
      this.root.dispose();
      this.root = null;
      this.chart = null;
      this.xAxis = null;
      this.timeCursorRange = null;
      this.axisIndexYAxisMap.clear();
      this.manualYAxisIndexes.clear();
      this.lastAxisValueRanges.clear();
      this.lastActiveAxisIndexes.clear();
      this.axisIndexLabelMap.clear();
      this.lineSeriesMap.clear();
      this.bandSeriesMap.clear();
      this.notificationSeries = null;
      this.alertPeakSeries = null;
      this.alertRangeItems = [];
      this.thresholdRangeItems = [];
      this.selectedRangeItem = null;
      this.noteRangeItems = [];
      this.lastPlotBounds = null;
      this.rangePickFirstTs = null;
      this.rangePickGuideItem = null;
    }
  }

  private buildChart(): void {
    const el = this.chartDiv?.nativeElement;
    if (!el) return;

    this.root = am5.Root.new(el);
    this.root.setThemes([am5themes_Animated.new(this.root)]);
    // Render the date axis (labels, tooltips and grid) in the user's profile timezone
    // rather than the machine's local timezone. Without this, a point the user clicks is
    // labelled in the browser's local timezone while the note dialog resolves it in the
    // profile timezone, so the two disagree when the timezones differ.
    const profileTimeZone = this.dateFormatService.getTimeZone();
    if (profileTimeZone) {
      this.root.timezone = am5.Timezone.new(profileTimeZone);
    }
    this.root.dateFormatter.set('dateFields', ['valueX']);

    this.chart = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
        panX: false, panY: false,
        // The mouse wheel over the plot zooms time; value axes scale from their own label area.
        wheelX: 'none', wheelY: 'zoomX',
        paddingRight: 8, paddingLeft: 0,
        // Extra bottom padding reserves room for the cursor date/time tooltip that sits
        // under the axis, so it is no longer clipped by the plot edge (DASH-3016).
        paddingTop: 8, paddingBottom: 26
      })
    );

    const xRenderer = am5xy.AxisRendererX.new(this.root, { minGridDistance: 60 });
    xRenderer.labels.template.setAll({ fontSize: 10, fill: am5.color(0x6B7280) });
    xRenderer.grid.template.setAll({ strokeOpacity: 0.08 });

    this.xAxis = this.chart.xAxes.push(
      am5xy.DateAxis.new(this.root, {
        // baseInterval is refined per-chart in syncSeries() to match the real sampling
        // cadence. A base far finer than the data (e.g. 1-minute vs ~10-minute cadence)
        // combined with groupData makes the grouped/visible slice misalign at deep zoom
        // and the plot renders empty (DASH-2846). Keep grouping enabled.
        baseInterval: this.computeBaseInterval(),
        groupData: true,
        groupCount: 500,
        // Snap zoom/pan instead of animating the axis range. The animated range morph is
        // what made the graph feel sluggish while zoomed in (DASH-3016) — regrouping a
        // large dataset every animation frame. Zero duration makes adjusting instant.
        interpolationDuration: 0,
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(this.root, {})
      })
    );
    const preferencesSubscription = this.dateFormatService.userPreferencesSubject.subscribe(
      () => this.ngZone.runOutsideAngular(() => this.applyUserPreferences()));
    this.xAxis.addDisposer(new am5.Disposer(() => preferencesSubscription.unsubscribe()));

    // Make the cursor date/time label readable and keep it clear of the plot edge. The
    // default axis tooltip used a small font and could sit half off the grid (DASH-3016);
    // give it a solid dark background, larger text and padding so it stays legible.
    const xAxisTooltip = this.xAxis.get('tooltip');
    if (xAxisTooltip) {
      xAxisTooltip.label.setAll({ fontSize: 12 });
      xAxisTooltip.setAll({ paddingTop: 5, paddingBottom: 5, paddingLeft: 8, paddingRight: 8 });
      xAxisTooltip.get('background')?.setAll({
        fill: am5.color(0x1F2937),
        fillOpacity: 0.95
      });
    }

    const cursor = am5xy.XYCursor.new(this.root, { behavior: 'zoomX', xAxis: this.xAxis });
    cursor.lineY.set('visible', false);
    cursor.events.on('cursormoved', () => {
      const now = Date.now();
      if (now - this.lastCursorEmit < 150) return;
      this.lastCursorEmit = now;
      const pos = cursor.getPrivate('positionX') as number | undefined;
      if (pos != null && this.xAxis) {
        const axisPos = this.xAxis.toAxisPosition(pos);
        const ts = this.xAxis.positionToDate(axisPos).getTime();
        this.ngZone.run(() => this.cursorMove.emit(ts));
      }
    });
    cursor.events.on('selectstarted', () => this.isCursorRangeSelecting = true);
    this.chart.set('cursor', cursor);

    this.chart.plotContainer.events.on('click', () => this.handlePlotClick());

    this.xAxis.onPrivate('selectionMinFinal', () => this.scheduleZoomEmit(this.isCursorRangeSelecting));
    this.xAxis.onPrivate('selectionMaxFinal', () => this.scheduleZoomEmit(this.isCursorRangeSelecting));

    // The horizontal scrollbar under the plot ("the bottom scale thing") was confusing and
    // added little — zooming is done by drag-selecting on the plot and reset via the Zoom Fit
    // toolbar button, so the scrollbar is omitted (DASH-3016).
    this.chart.plotContainer.events.on('boundschanged', () => this.emitPlotBounds());

    this.initializeYAxes();
    this.syncSeries();
    this.syncAlertPeriods();
    this.syncNotifications();
    this.syncThresholds();
    this.syncSelectedRange();
    this.syncNoteRanges();
    this.emitPlotBounds();
    this.applyRangePickMode();

  }

  private applyUserPreferences(): void {
    if (!this.root || !this.xAxis) return;
    this.xAxis.set('tooltipDateFormats', this.buildTooltipDateFormats());
    this.root.dateFormatter.set('dateFormat', this.dateFormatService.userDateWithTimeFormat);

    const timeZone = this.dateFormatService.getTimeZone();
    if (timeZone && timeZone !== this.root.timezone?.name) {
      this.root.timezone = am5.Timezone.new(timeZone);
      this.syncSeries();
    }
    this.syncAlertPeriods();
    this.syncNotifications();
  }

  // Always show the full date + time in the axis (cursor) tooltip, in the user's date order
  // and 12/24h preference. amCharts' default tooltipDateFormats drop the date at fine zoom
  // (minute/hour render only "HH:mm"), so once zoomed in the cursor label showed a bare time
  // with no date and was hard to place on the timeline. The year is kept at every zoom level.
  private buildTooltipDateFormats(): Record<am5.time.TimeUnit, string> {
    const date = this.dateFormatService.userDateFormat;
    const time = this.dateFormatService.userTimeFormat;
    return {
      millisecond: `${date} ${this.dateFormatService.userTimeWithMillisecondsFormat}`,
      second: `${date} ${this.dateFormatService.userTimeWithSecondsFormat}`,
      minute: `${date} ${time}`,
      hour: `${date} ${time}`,
      day: date,
      week: date,
      month: this.dateFormatService.monthYearFormat,
      year: 'yyyy'
    };
  }

  protected handlePlotClick(): void {
    if (!this.rangePickMode || !this.xAxis || !this.chart) {
      return;
    }

    const cursor = this.chart.get('cursor');
    const positionX = cursor?.getPrivate('positionX') as number | undefined;
    if (positionX == null) {
      return;
    }

    const axisPosition = this.xAxis.toAxisPosition(positionX);
    const timestamp = this.xAxis.positionToDate(axisPosition).getTime();
    if (!Number.isFinite(timestamp)) {
      return;
    }

    if (this.rangePickFirstTs == null) {
      this.rangePickFirstTs = timestamp;
      this.showRangePickGuide(timestamp);
      this.ngZone.run(() => this.rangePickProgress.emit(timestamp));
      return;
    }

    const range = this.resolvePickedRange(this.rangePickFirstTs, timestamp);
    this.rangePickFirstTs = null;
    this.clearRangePickGuide();
    this.ngZone.run(() => {
      this.rangePickProgress.emit(null);
      if (range) {
        this.rangePicked.emit(range);
      }
    });
  }

  private resolvePickedRange(first: number, second: number): DateTimeRange | null {
    if (!Number.isFinite(first) || !Number.isFinite(second)) {
      return null;
    }

    const start = Math.min(first, second);
    const end = Math.max(first, second);
    if (end <= start) {
      return null;
    }

    return { start, end };
  }

  protected applyRangePickMode(): void {
    if (!this.chart) {
      return;
    }

    const cursor = this.chart.get('cursor');
    cursor?.set('behavior', this.rangePickMode ? 'none' : 'zoomX');

    if (!this.rangePickMode) {
      this.rangePickFirstTs = null;
      this.clearRangePickGuide();
    }
  }

  private showRangePickGuide(timestamp: number): void {
    if (!this.xAxis) {
      return;
    }

    this.clearRangePickGuide();
    const guideItem = this.xAxis.makeDataItem({ value: timestamp });
    this.xAxis.createAxisRange(guideItem);
    guideItem.get('grid')?.setAll({
      visible: true,
      stroke: am5.color(0xDC2626),
      strokeWidth: 2,
      strokeDasharray: [4, 2],
    });
    this.rangePickGuideItem = guideItem;
  }

  private clearRangePickGuide(): void {
    if (this.rangePickGuideItem && this.xAxis) {
      this.xAxis.axisRanges.removeValue(this.rangePickGuideItem);
    }
    this.rangePickGuideItem = null;
  }

  private syncSeries(): void {
    if (!this.chart || !this.xAxis || !this.root) {
      return;
    }
    const chart: am5xy.XYChart = this.chart;
    const xAxis: am5xy.DateAxis<am5xy.AxisRendererX> = this.xAxis;
    const root: am5.Root = this.root;

    // Refine the date-axis baseInterval to this chart's actual sampling cadence so the
    // grouped dataset stays aligned with the zoom-selection window at deep zoom (DASH-2846).
    const baseInterval = this.computeBaseInterval();
    const currentInterval = xAxis.get('baseInterval');
    if (!currentInterval || currentInterval.timeUnit !== baseInterval.timeUnit || currentInterval.count !== baseInterval.count) {
      xAxis.set('baseInterval', baseInterval);
    }

    // Per-axis value ranges, used to pin each value axis to an explicit min/max below.
    const axisValueRanges = new Map<number, { min: number; max: number }>();

    const unitOrder: string[] = [];
    this.seriesData
      .filter((series) => !series.hidden)
      .forEach((series) => {
        if (!unitOrder.includes(series.paramUnit) && unitOrder.length < 3) {
          unitOrder.push(series.paramUnit);
        }
      });

    const activeKeys = new Set(this.seriesData.map((series) => this.getSeriesKey(series)));
    this.lineSeriesMap.forEach((lineSeries, key) => {
      if (!activeKeys.has(key)) {
        lineSeries.dispose();
        this.lineSeriesMap.delete(key);
      }
    });
    this.bandSeriesMap.forEach((bandSeries, key) => {
      if (!activeKeys.has(key)) {
        bandSeries.dispose();
        this.bandSeriesMap.delete(key);
      }
    });

    const axisLabels = new Map<number, string>();
    const activeAxisIndexes = new Set<number>();
    this.seriesData.forEach((seriesInput) => {
      let axisIdx = seriesInput.axisOverride ?? unitOrder.indexOf(seriesInput.paramUnit);
      if (axisIdx < 0 || axisIdx > 2) {
        axisIdx = 0;
      }

      const yAxis = this.axisIndexYAxisMap.get(axisIdx);
      if (!yAxis) {
        return;
      }

      axisLabels.set(axisIdx, seriesInput.paramUnit);

      const hasFiniteValue = seriesInput.data.some((point) => typeof point.value === 'number' && Number.isFinite(point.value));
      if (!seriesInput.hidden && hasFiniteValue) {
        activeAxisIndexes.add(axisIdx);
        seriesInput.data.forEach((point) => {
          if (typeof point.value === 'number' && Number.isFinite(point.value)) {
            const existing = axisValueRanges.get(axisIdx);
            if (!existing) {
              axisValueRanges.set(axisIdx, { min: point.value, max: point.value });
            } else {
              existing.min = Math.min(existing.min, point.value);
              existing.max = Math.max(existing.max, point.value);
            }
          }
        });
      }

      const colorInt = this.hexToColorInt(seriesInput.color, this.COLORS[axisIdx % this.COLORS.length]);
      const seriesColor = am5.color(colorInt);
      const dash = this.PARAMETER_DASHES[(seriesInput.parameterIndex ?? 0) % this.PARAMETER_DASHES.length];
      const name = seriesInput.nodeName
        ? `${this.stripTooltipTokens(seriesInput.nodeName)} — ${this.stripTooltipTokens(seriesInput.paramName)}`
        : this.stripTooltipTokens(seriesInput.paramName);
      const safeUnit = this.stripTooltipTokens(seriesInput.paramUnit);
      const key = this.getSeriesKey(seriesInput);

      const chartData = this.buildChartPoints(seriesInput);

      // Min/max band, rendered BEHIND the mean line: a fill between each group's high and
      // low value so peaks/troughs stay visible when zoomed out (the mean line alone would
      // smooth them away). Both edges are grouped from the SAME `value` field — high -> valueY,
      // low -> openValueY — so at full zoom-in (group size 1) high === low and the band
      // collapses to nothing. Stroke hidden; only the fill shows. Toggled by the Min/max layer.
      let band = this.bandSeriesMap.get(key);
      if (!band) {
        band = chart.series.push(
          am5xy.LineSeries.new(root, {
            name: `${name} (min/max)`,
            xAxis,
            yAxis,
            valueXField: 'timestamp',
            valueYField: 'value',
            openValueYField: 'value',
            valueYGrouped: 'high',
            openValueYGrouped: 'low',
            connect: false,
            // Snap on regroup rather than animating every point — keeps deep-zoom
            // adjusting responsive on large datasets (DASH-3016).
            interpolationDuration: 0,
          })
        );
        band.strokes.template.setAll({ strokeOpacity: 0 });
        this.bandSeriesMap.set(key, band);
      }
      band.set('yAxis', yAxis);
      band.fills.template.setAll({ visible: true, fillOpacity: 0.14, fill: seriesColor });
      // Only populate + show the band when its layer is enabled. Grouping a second full
      // series over the raw data is expensive on long ranges (a quarter of 30s data is
      // ~260k points/series), so while the layer is off we leave the band empty and pay
      // nothing — it is populated on demand when the layer is turned on.
      if (this.showMinMaxBand && !seriesInput.hidden) {
        band.data.setAll(chartData);
        band.show(0);
      } else {
        band.data.setAll([]);
        band.hide(0);
      }

      let series = this.lineSeriesMap.get(key);
      if (!series) {
        series = chart.series.push(
          am5xy.LineSeries.new(root, {
            name,
            xAxis,
            yAxis,
            valueXField: 'timestamp',
            valueYField: 'value',
            // The line traces the group AVERAGE (a clean trend); peaks/troughs are surfaced
            // by the min/max band (toggle), not per-sample bullets (DASH-2846).
            valueYGrouped: 'average',
            // Keep connect:false so offline gaps stay visible as gaps — the line must
            // NOT bridge across missing sensor data (DASH-2846).
            connect: false,
            // Snap on regroup rather than animating every point — keeps deep-zoom
            // adjusting responsive on large datasets (DASH-3016).
            interpolationDuration: 0,
            tooltip: am5.Tooltip.new(root, {
              labelText: `{name}: {valueY.formatNumber('#.##')} ${safeUnit}`
            })
          })
        );
        // No per-sample bullets on the value line. The deep-zoom "plot vanishing" fix is the
        // dynamic baseInterval + manual Y-axis bounds (see computeBaseInterval / applyYAxisBounds),
        // NOT bullets — so drawing a sprite per sample only reintroduced the zoom stall for no
        // added correctness. Individual-sample visibility, if ever needed, belongs behind an
        // opt-in toggle. groupData/connect:false unchanged. (DASH-2846)
        this.lineSeriesMap.set(key, series);
      }

      series.setAll({
        name,
        xAxis,
        yAxis,
        stroke: seriesColor,
        fill: seriesColor,
      });
      series.strokes.template.setAll({
        stroke: seriesColor,
        strokeWidth: 2,
        strokeDasharray: dash,
      });
      series.fills.template.setAll({ visible: false });

      const tooltip = series.get('tooltip');
      if (tooltip) {
        tooltip.label.set('text', `{name}: {valueY.formatNumber('#.##')} ${safeUnit}`);
        tooltip.get('background')?.setAll({
          fill: seriesColor,
          stroke: seriesColor,
          fillOpacity: 0.9,
          strokeOpacity: 1,
        });
        tooltip.label.setAll({ fill: am5.color(0xFFFFFF) });
      }

      series.data.setAll(chartData);

      if (seriesInput.hidden) {
        series.hide(0);
      } else {
        series.show(0);
      }
    });

    this.updateAxisLabels(axisLabels, activeAxisIndexes);
    this.applyYAxisBounds(axisValueRanges, activeAxisIndexes);
    this.emitPlotBounds();
  }

  /**
   * Projects a series' raw points to the `{ timestamp, value }[]` amCharts consumes, dropping
   * null-value samples. Shared by the line and band data setters so the projection stays in
   * one place if `ChartDataPoint` gains fields.
   */
  private buildChartPoints(seriesInput: ChartSeriesInput): { timestamp: number; value: number }[] {
    return seriesInput.data
      .filter((point) => point.value != null)
      .map((point) => ({ timestamp: point.timestamp, value: point.value as number }));
  }

  /**
   * Populates + shows each min/max band when the layer is turned on, and empties + hides it
   * when turned off. Populating groups a second full series over the raw data (expensive on
   * long ranges), so it is deferred to here rather than paid on every render — the band costs
   * nothing until the user enables it. A band stays empty/hidden while its series is hidden.
   */
  private applyBandVisibility(): void {
    this.seriesData.forEach((seriesInput) => {
      const band = this.bandSeriesMap.get(this.getSeriesKey(seriesInput));
      if (!band) {
        return;
      }
      if (this.showMinMaxBand && !seriesInput.hidden) {
        band.data.setAll(this.buildChartPoints(seriesInput));
        band.show(0);
      } else {
        band.data.setAll([]);
        band.hide(0);
      }
    });
  }

  /**
   * Pins each value axis to an explicit min/max derived from its series' data instead of
   * leaving it on auto. With auto scaling + grouped data, a deep X zoom can recompute the
   * value axis from a degenerate grouped slice and collapse the plot to empty (DASH-2846).
   * A stable, manually-set range keeps the line visible while zooming. Inactive axes are
   * returned to auto so they don't force a range with no data. Axes under a manual Y-range
   * override are skipped entirely so the user's range survives data/series/zoom updates.
   */
  private applyYAxisBounds(
    axisValueRanges: Map<number, { min: number; max: number }>,
    activeAxisIndexes: Set<number>,
  ): void {
    // Cache the latest data extents so resetYAxisRange()/zoomFit() can recompute the
    // auto range without re-running syncSeries().
    this.lastAxisValueRanges = axisValueRanges;
    this.lastActiveAxisIndexes = activeAxisIndexes;
    this.axisIndexYAxisMap.forEach((yAxis, axisIdx) => {
      if (this.manualYAxisIndexes.has(axisIdx)) {
        return;
      }
      const range = axisValueRanges.get(axisIdx);
      if (!activeAxisIndexes.has(axisIdx) || !range || !Number.isFinite(range.min) || !Number.isFinite(range.max)) {
        yAxis.set('min', undefined);
        yAxis.set('max', undefined);
        return;
      }

      const span = range.max - range.min;
      const padding = span > 0 ? span * 0.08 : (Math.abs(range.max) || 1) * 0.08;
      let min = range.min - padding;
      const max = range.max + padding;
      // Don't drop a non-negative dataset below zero — negative concentrations are noise.
      if (range.min >= 0 && min < 0) {
        min = 0;
      }

      yAxis.set('min', min);
      yAxis.set('max', max);
    });
  }

  /**
   * Derives a DateAxis baseInterval from the real sampling cadence of the current series
   * (the median gap between consecutive samples), so grouping stays aligned with the data
   * at any zoom level. Falls back to 1 minute when there aren't enough points to measure.
   */
  protected computeBaseInterval(): { timeUnit: am5.time.TimeUnit; count: number } {
    const deltas = this.seriesData
      .filter((series) => !series.hidden)
      .flatMap((series) =>
        positiveDeltas(
          series.data
            .filter((point) => point.value != null && Number.isFinite(point.timestamp))
            .map((point) => point.timestamp)
            .sort((a, b) => a - b),
        ),
      );

    const medianDelta = upperMedian(deltas);
    if (medianDelta === null) {
      return { timeUnit: 'minute', count: 1 };
    }
    return msToBaseInterval(medianDelta, { timeUnit: 'minute', count: 1 });
  }

  private hexToColorInt(hex: string | undefined, fallbackColor: number): number {
    if (!hex) {
      return fallbackColor;
    }

    const normalizedHex = hex.trim().replace('#', '');
    if (!/^[0-9a-fA-F]{6}$/.test(normalizedHex)) {
      return fallbackColor;
    }

    return parseInt(normalizedHex, 16);
  }

  private initializeYAxes(): void {
    if (!this.chart || !this.root) {
      return;
    }

    for (let axisIdx = 0; axisIdx < 3; axisIdx += 1) {
      const colorInt = this.COLORS[axisIdx % this.COLORS.length];
      const color = am5.color(colorInt);
      const renderer = am5xy.AxisRendererY.new(this.root, {
        opposite: axisIdx === 1,
        marginLeft: axisIdx >= 2 ? 20 : 0,
        // Dragging in the axis label area scales this axis only, so each axis in multi-axis
        // or compare mode can be adjusted on its own.
        pan: 'zoom',
      });
      renderer.labels.template.setAll({ fill: color, fontSize: 10 });
      renderer.grid.template.setAll({ strokeOpacity: axisIdx === 0 ? 0.08 : 0.03 });

      const axisTooltip = am5.Tooltip.new(this.root, {});
      axisTooltip.label.adapters.add('text', (rawText) => {
        if (!rawText || /nan/i.test(rawText)) {
          return '';
        }
        return rawText;
      });

      const yAxis = this.chart.yAxes.push(
        am5xy.ValueAxis.new(this.root, {
          renderer,
          tooltip: axisTooltip,
        })
      ) as am5xy.ValueAxis<am5xy.AxisRendererY>;

      const axisLabel = am5.Label.new(this.root, {
        rotation: -90,
        text: '',
        fill: color,
        fontSize: 9,
        y: am5.p50,
        centerX: am5.p50,
      });
      yAxis.children.unshift(axisLabel);

      this.axisIndexYAxisMap.set(axisIdx, yAxis);
      this.axisIndexLabelMap.set(axisIdx, axisLabel);

      yAxis.on('start', () => this.scheduleYAxisSettle(axisIdx));
      yAxis.on('end', () => this.scheduleYAxisSettle(axisIdx));
      renderer.thumb?.setAll({ wheelable: true, cursorOverStyle: 'ns-resize' });
      renderer.thumb?.events.on('wheel', (event) => this.scaleYAxisByWheel(axisIdx, event.originalEvent));
    }
  }

  /**
   * An interactive scale zooms the axis within its pinned min/max (start/end change). Once the
   * drag settles, the visible window becomes the axis' manual min/max and the zoom is cleared,
   * so applyYAxisBounds() leaves it alone on later renders and the typed inputs show it.
   */
  private scheduleYAxisSettle(axisIndex: number): void {
    if (this.isSettlingYAxis) {
      return;
    }
    const pending = this.yAxisSettleTimers.get(axisIndex);
    if (pending) {
      clearTimeout(pending);
    }
    this.yAxisSettleTimers.set(axisIndex, setTimeout(() => {
      this.yAxisSettleTimers.delete(axisIndex);
      this.settleYAxisZoom(axisIndex);
    }, 250));
  }

  private settleYAxisZoom(axisIndex: number): void {
    const yAxis = this.axisIndexYAxisMap.get(axisIndex);
    if (!yAxis) {
      return;
    }
    // A drag can widen the axis past its range (start < 0 / end > 1) as well as narrow it.
    const start = yAxis.get('start') ?? 0;
    const end = yAxis.get('end') ?? 1;
    if (start === 0 && end === 1) {
      return;
    }
    const min = yAxis.getPrivate('selectionMin') as number | undefined;
    const max = yAxis.getPrivate('selectionMax') as number | undefined;
    if (min == null || max == null || !Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
      return;
    }
    this.applyInteractiveYRange(axisIndex, yAxis, min, max);
  }

  /** Mouse wheel over an axis' label area scales it around its centre; unlike the drag it can also widen the range. */
  private scaleYAxisByWheel(axisIndex: number, event: WheelEvent): void {
    const yAxis = this.axisIndexYAxisMap.get(axisIndex);
    if (!yAxis || !this.lastActiveAxisIndexes.has(axisIndex) || event.deltaY === 0) {
      return;
    }
    event.preventDefault();
    const min = yAxis.getPrivate('selectionMin') as number | undefined;
    const max = yAxis.getPrivate('selectionMax') as number | undefined;
    if (min == null || max == null || !Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
      return;
    }
    const factor = event.deltaY > 0 ? NodeAnalyticsChartComponent.WHEEL_SCALE_STEP : 1 / NodeAnalyticsChartComponent.WHEEL_SCALE_STEP;
    const centre = (min + max) / 2;
    const half = ((max - min) / 2) * factor;
    this.applyInteractiveYRange(axisIndex, yAxis, centre - half, centre + half);
  }

  private applyInteractiveYRange(axisIndex: number, yAxis: am5xy.ValueAxis<am5xy.AxisRendererY>, min: number, max: number): void {
    this.isSettlingYAxis = true;
    this.manualYAxisIndexes.add(axisIndex);
    yAxis.setAll({ min, max, start: 0, end: 1 });
    this.isSettlingYAxis = false;
    this.ngZone.run(() => this.yAxisRangeChange.emit(axisIndex));
  }

  private updateAxisLabels(axisLabels: Map<number, string>, activeAxisIndexes: Set<number>): void {
    this.axisIndexLabelMap.forEach((label, axisIdx) => {
      const isAxisActive = activeAxisIndexes.has(axisIdx);
      label.set('text', axisLabels.get(axisIdx) ?? '');

      const yAxis = this.axisIndexYAxisMap.get(axisIdx);
      const tooltip = yAxis?.get('tooltip');
      if (tooltip) {
        tooltip.set('forceHidden', !isAxisActive);
      }
    });
  }

  private syncNotifications(): void {
    if (!this.chart || !this.xAxis || !this.root) {
      return;
    }
    const chart: am5xy.XYChart = this.chart;
    const xAxis: am5xy.DateAxis<am5xy.AxisRendererX> = this.xAxis;
    const root: am5.Root = this.root;

    const yAxis = this.axisIndexYAxisMap.get(0);
    if (!yAxis) {
      return;
    }

    if (!this.notificationSeries) {
      this.notificationSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
          xAxis,
          yAxis,
          valueXField: 'timestamp',
          valueYField: 'value',
          userData: 'notifications',
        })
      );

      this.notificationSeries.strokes.template.setAll({ strokeOpacity: 0 });
      this.notificationSeries.fills.template.setAll({ visible: false });
      this.notificationSeries.bullets.push(() =>
        am5.Bullet.new(root, {
          sprite: am5.Circle.new(root, {
            radius: 5,
            tooltipText: '{tooltipText}',
            strokeWidth: 1,
            templateField: 'bulletSettings',
          })
        })
      );
    }

    if (!this.showNotifications) {
      this.notificationSeries.data.setAll([]);
      return;
    }

    const notificationData = this.notifications
      .filter((notification) => Number.isFinite(notification.timestamp) && notification.timestamp > 0)
      .map((notification) => {
        const color = am5.color(this.getSeverityColor(notification.severity));
        const sentLabel = this.toTooltipDate(notification.sentAt || notification.timestamp);
        const label = this.stripTooltipTokens(notification.label);
        const channelLabel = notification.channel ? `\nChannel: ${this.stripTooltipTokens(notification.channel)}` : '';
        return {
          timestamp: notification.timestamp,
          value: 0,
          tooltipText: `${label}\nSeverity: ${alertSeverityLabel(notification.severity)}\nState: ${this.getStateLabel(notification.state)}\nSent: ${sentLabel}${channelLabel}`,
          bulletSettings: {
            fill: color,
            stroke: am5.color(0xFFFFFF),
          },
        };
      })
      // amCharts XY LineSeries require data ascending by valueX (timestamp);
      // sort defensively so bullets/tooltips land at the correct x-position.
      .sort((left, right) => left.timestamp - right.timestamp);

    this.notificationSeries.data.setAll(notificationData);
  }

  private syncAlertPeriods(): void {
    if (!this.xAxis || !this.root || !this.chart) {
      return;
    }

    this.alertRangeItems.forEach((dataItem) => this.xAxis?.axisRanges.removeValue(dataItem));
    this.alertRangeItems = [];

    if (!this.alertPeakSeries) {
      const yAxis = this.axisIndexYAxisMap.get(0);
      if (!yAxis) {
        return;
      }

      this.alertPeakSeries = this.chart.series.push(
        am5xy.LineSeries.new(this.root, {
          xAxis: this.xAxis,
          yAxis,
          valueXField: 'timestamp',
          valueYField: 'value',
        })
      );
      this.alertPeakSeries.strokes.template.setAll({ strokeOpacity: 0 });
      this.alertPeakSeries.fills.template.setAll({ visible: false });
      this.alertPeakSeries.bullets.push(() =>
        am5.Bullet.new(this.root as am5.Root, {
          sprite: am5.Triangle.new(this.root as am5.Root, {
            width: 10,
            height: 10,
            rotation: 180,
            tooltipText: '{tooltipText}',
            templateField: 'bulletSettings',
          })
        })
      );
    }

    if (!this.showAlertPeriods) {
      this.alertPeakSeries.data.setAll([]);
      return;
    }

    const peakData: Array<{ timestamp: number; value: number; tooltipText: string; bulletSettings: { fill: am5.Color; stroke: am5.Color } }> = [];

    this.alertPeriods
      .filter((period) => Number.isFinite(period.startedAt) && Number.isFinite(period.endedAt) && period.endedAt >= period.startedAt)
      .forEach((period) => {
        const rangeDataItem = this.xAxis?.makeDataItem({
          value: period.startedAt,
          endValue: period.endedAt,
        });

        if (!rangeDataItem) {
          return;
        }

        this.xAxis?.createAxisRange(rangeDataItem);
        this.alertRangeItems.push(rangeDataItem);

        const color = am5.color(this.getSeverityColor(period.severity));
        const fillOpacity = period.severity === AlertSeverityLevel.Critical ? 0.26 : 0.16;
        const periodName = this.stripTooltipTokens(period.notificationName);
        const tooltipText = [
          periodName,
          `Severity: ${alertSeverityLabel(period.severity)}`,
          `State: ${this.getStateLabel(period.state)}`,
          `Started: ${this.toTooltipDate(period.startedAt)}`,
          `Ended: ${this.toTooltipDate(period.endedAt)}`,
          period.sentAt ? `Sent: ${this.toTooltipDate(period.sentAt)}` : '',
          period.peakAt != null && period.peakValue != null
            ? `Peak: ${period.peakValue} @ ${this.toTooltipDate(period.peakAt)}`
            : 'Peak: N/A',
        ].filter(Boolean).join('\n');

        rangeDataItem.get('axisFill')?.setAll({
          visible: true,
          fill: color,
          fillOpacity,
          tooltipText,
        });

        if (period.severity === AlertSeverityLevel.Critical) {
          rangeDataItem.get('axisFill')?.animate({
            key: 'fillOpacity',
            from: 0.18,
            to: 0.3,
            duration: 900,
            loops: Infinity,
          });
        }

        rangeDataItem.get('grid')?.setAll({
          visible: true,
          stroke: color,
          strokeOpacity: 0.5,
          strokeWidth: 1,
        });

        if (Number.isFinite(period.peakAt) && Number.isFinite(period.peakValue)) {
          const peakAt = period.peakAt as number;
          peakData.push({
            timestamp: peakAt,
            value: period.peakValue as number,
            tooltipText: `${periodName}\nPeak: ${period.peakValue}\nTime: ${this.toTooltipDate(peakAt)}`,
            bulletSettings: {
              fill: color,
              stroke: am5.color(0xFFFFFF),
            },
          });
        }
      });

    // amCharts XY LineSeries require data ascending by valueX (timestamp);
    // alertPeriods arrive in descending date order, so sort peak bullets here.
    peakData.sort((left, right) => left.timestamp - right.timestamp);
    this.alertPeakSeries.data.setAll(peakData);
  }

  private syncThresholds(): void {
    const yAxis = this.axisIndexYAxisMap.get(0);
    if (!yAxis || !this.root) {
      return;
    }

    this.thresholdRangeItems.forEach((dataItem) => yAxis.axisRanges.removeValue(dataItem));
    this.thresholdRangeItems = [];

    if (!this.showThresholds) {
      return;
    }

    this.thresholds
      .filter((threshold) => Number.isFinite(threshold.value))
      .forEach((threshold) => {
        const rangeDataItem = yAxis.makeDataItem({ value: threshold.value });
        yAxis.createAxisRange(rangeDataItem);
        this.thresholdRangeItems.push(rangeDataItem);

        const colorInt = this.hexToColorInt(threshold.colorHex, this.getSeverityColor(threshold.severity));
        const color = am5.color(colorInt);
        rangeDataItem.get('grid')?.setAll({
          visible: true,
          stroke: color,
          strokeOpacity: 0.8,
          strokeWidth: 1,
          strokeDasharray: [6, 4],
        });
        rangeDataItem.get('label')?.setAll({
          text: `${this.stripTooltipTokens(threshold.label)}: ${threshold.value}`,
          fill: color,
          background: am5.RoundedRectangle.new(this.root as am5.Root, {
            fill: am5.color(0xFFFFFF),
            fillOpacity: 0.9,
          }),
        });
      });
  }

  private syncSelectedRange(): void {
    if (!this.xAxis || !this.root) {
      return;
    }

    if (this.selectedRangeItem) {
      this.xAxis.axisRanges.removeValue(this.selectedRangeItem);
      this.selectedRangeItem = null;
    }

    if (!this.selectedRange || !Number.isFinite(this.selectedRange.start) || !Number.isFinite(this.selectedRange.end)) {
      return;
    }

    const start = Math.min(this.selectedRange.start, this.selectedRange.end);
    const end = Math.max(this.selectedRange.start, this.selectedRange.end);
    if (end <= start) {
      return;
    }

    const rangeDataItem = this.xAxis.makeDataItem({
      value: start,
      endValue: end,
    });

    this.xAxis.createAxisRange(rangeDataItem);
    this.selectedRangeItem = rangeDataItem;

    rangeDataItem.get('axisFill')?.setAll({
      visible: true,
      fill: am5.color(0x5B6BD5),
      fillOpacity: 0.2,
    });
    rangeDataItem.get('grid')?.setAll({
      visible: true,
      stroke: am5.color(0x5B6BD5),
      strokeOpacity: 0.6,
      strokeDasharray: [3, 3],
      strokeWidth: 1,
    });
  }

  private syncNoteRanges(): void {
    if (!this.xAxis || !this.root) {
      return;
    }

    this.noteRangeItems.forEach((dataItem) => this.xAxis?.axisRanges.removeValue(dataItem));
    this.noteRangeItems = [];

    if (!this.showNotes) {
      return;
    }

    this.noteRanges
      .filter((range) => Number.isFinite(range.start) && Number.isFinite(range.end) && range.end > range.start)
      .forEach((range) => {
        const rangeDataItem = this.xAxis?.makeDataItem({
          value: range.start,
          endValue: range.end,
        });

        if (!rangeDataItem) {
          return;
        }

        this.xAxis?.createAxisRange(rangeDataItem);
        this.noteRangeItems.push(rangeDataItem);

        const isSelected = this.selectedNoteId === range.id;
        rangeDataItem.get('axisFill')?.setAll({
          visible: true,
          fill: am5.color(0xDC2626),
          fillOpacity: isSelected ? 0.38 : 0.2,
        });
        rangeDataItem.get('grid')?.setAll({
          visible: true,
          stroke: am5.color(0xDC2626),
          strokeOpacity: isSelected ? 0.8 : 0.45,
          strokeDasharray: [3, 3],
          strokeWidth: isSelected ? 2 : 1,
        });
      });
  }

  // Each severity maps to its own design token so the four levels stay visually
  // distinct on the chart instead of collapsing into two amber/red buckets
  // (DASH-2844). Fallbacks mirror the token values for canvas/headless contexts
  // where the CSS variable can't be resolved.
  private static readonly SEVERITY_COLOR_TOKENS: Record<AlertSeverityLevel, { token: string; fallback: number }> = {
    [AlertSeverityLevel.Watch]: { token: '--severity-watch', fallback: 0xd97706 },
    [AlertSeverityLevel.Action]: { token: '--severity-action', fallback: 0xea580c },
    [AlertSeverityLevel.Alert]: { token: '--severity-alert', fallback: 0xdc2626 },
    [AlertSeverityLevel.Critical]: { token: '--severity-critical', fallback: 0xb91c1c },
  };

  private readonly UNKNOWN_SEVERITY_COLOR = 0x6b7280;

  private static readonly WHEEL_SCALE_STEP = 1.2;

  private isKnownSeverity(severity: AlertSeverityLevel | number): severity is AlertSeverityLevel {
    return severity === AlertSeverityLevel.Watch ||
      severity === AlertSeverityLevel.Action ||
      severity === AlertSeverityLevel.Alert ||
      severity === AlertSeverityLevel.Critical;
  }

  private getSeverityColor(severity: AlertSeverityLevel | number): number {
    if (!this.isKnownSeverity(severity)) {
      return this.UNKNOWN_SEVERITY_COLOR;
    }

    const mapping = NodeAnalyticsChartComponent.SEVERITY_COLOR_TOKENS[severity];
    if (!mapping) {
      return this.UNKNOWN_SEVERITY_COLOR;
    }

    return this.hexToColorInt(this.resolveCssVariable(mapping.token), mapping.fallback);
  }

  // amCharts renders on canvas and can't consume `var(--x)` directly, so resolve
  // the token to its computed hex first (same approach as DASH-2354).
  private resolveCssVariable(variableName: string): string | undefined {
    if (typeof window === 'undefined') {
      return undefined;
    }

    return (
      getComputedStyle(document.documentElement).getPropertyValue(variableName).trim() || undefined
    );
  }

  private getStateLabel(state: AlertEventState): string {
    if (state === AlertEventState.Resolved) {
      return 'Resolved';
    }
    if (state === AlertEventState.Acknowledged) {
      return 'Acknowledged';
    }
    if (state === AlertEventState.Escalated) {
      return 'Escalated';
    }
    return 'Active';
  }

  private toTooltipDate(timestamp: number): string {
    if (!Number.isFinite(timestamp)) {
      return 'N/A';
    }
    return this.dateFormatService.formatTimestampToString(timestamp);
  }

  // amCharts resolves {token} placeholders inside tooltip text against the data
  // item, so strip braces from API-sourced fields (notification name, channel)
  // before embedding them — a stray {name}/{valueY} would otherwise render as an
  // unexpected token rather than literal text.
  private stripTooltipTokens(value: string): string {
    return (value ?? '').replace(/[{}]/g, '');
  }

  private getSeriesKey(series: ChartSeriesInput): string {
    return `${series.nodeId}:${series.paramId}`;
  }

  private updateTimeCursorLine(): void {
    if (!this.xAxis || !this.chart) return;
    if (this.timeCursorRange) {
      this.timeCursorRange.set('value', this.timeCursor);
      return;
    }
    this.timeCursorRange = this.xAxis.makeDataItem({});
    this.xAxis.createAxisRange(this.timeCursorRange);
    this.timeCursorRange.set('value', this.timeCursor);
    this.timeCursorRange.get('grid')?.setAll({
      stroke: am5.color(0x5B6BD5),
      strokeWidth: 2,
      strokeDasharray: [4, 2],
      visible: true
    });
  }

  private scheduleZoomEmit(emitRangeSelection = false): void {
    if (this.zoomTimer) clearTimeout(this.zoomTimer);
    this.zoomTimer = setTimeout(() => {
      if (!this.xAxis) return;
      const selMin = this.xAxis.getPrivate('selectionMin') as number | undefined;
      const selMax = this.xAxis.getPrivate('selectionMax') as number | undefined;
      const absMin = this.xAxis.getPrivate('min') as number | undefined;
      const absMax = this.xAxis.getPrivate('max') as number | undefined;

      if (selMin == null || selMax == null) {
        this.isCursorRangeSelecting = false;
        return;
      }
      if (this.isNearFullRangeSelection(selMin, selMax, absMin, absMax)) {
        this.isCursorRangeSelecting = false;
        this.ngZone.run(() => this.zoomChange.emit(null));
        return;
      }

      this.ngZone.run(() => {
        const range = { start: selMin, end: selMax };
        this.zoomChange.emit(range);
        if (emitRangeSelection) this.rangeSelection.emit(range);
      });
      this.isCursorRangeSelecting = false;
    }, 150);
  }

  private isNearFullRangeSelection(
    selMin: number,
    selMax: number,
    absMin?: number,
    absMax?: number,
  ): boolean {
    if (selMax <= selMin) {
      return true;
    }

    if (absMin == null || absMax == null || absMax <= absMin) {
      const axisStart = this.xAxis?.get('start');
      const axisEnd = this.xAxis?.get('end');
      return axisStart != null && axisEnd != null && axisStart <= 0.01 && axisEnd >= 0.99;
    }

    const totalRange = absMax - absMin;
    const selectedRange = selMax - selMin;
    const leftGap = Math.max(0, selMin - absMin);
    const rightGap = Math.max(0, absMax - selMax);
    const selectedCoverage = selectedRange / totalRange;
    const leftGapRatio = leftGap / totalRange;
    const rightGapRatio = rightGap / totalRange;

    return selectedCoverage >= 0.995 || (leftGapRatio <= 0.005 && rightGapRatio <= 0.005);
  }

  private emitPlotBounds(): void {
    if (!this.chart || !this.chartDiv?.nativeElement) {
      return;
    }

    const hostRect = this.chartDiv.nativeElement.getBoundingClientRect();
    if (!Number.isFinite(hostRect.width) || hostRect.width <= 0) {
      return;
    }

    const plotRect = this.chart.plotContainer.globalBounds();
    const leftPx = this.clamp(plotRect.left - hostRect.left, 0, hostRect.width);
    const rightPx = this.clamp(hostRect.right - plotRect.right, 0, hostRect.width);
    const widthPx = Math.max(0, hostRect.width - leftPx - rightPx);
    const nextBounds: ChartPlotBounds = {
      leftPx,
      rightPx,
      widthPx,
    };

    if (
      this.lastPlotBounds &&
      Math.abs(this.lastPlotBounds.leftPx - nextBounds.leftPx) < 0.5 &&
      Math.abs(this.lastPlotBounds.rightPx - nextBounds.rightPx) < 0.5 &&
      Math.abs(this.lastPlotBounds.widthPx - nextBounds.widthPx) < 0.5
    ) {
      return;
    }

    this.lastPlotBounds = nextBounds;
    this.ngZone.run(() => this.plotBoundsChange.emit(nextBounds));
  }

  private clamp(value: number, min: number, max: number): number {
    if (value < min) {
      return min;
    }
    if (value > max) {
      return max;
    }
    return value;
  }
}
